import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  SEARCH_ALL, SEARCH_MODULES, SEARCH_PAUSE_MS, groupResults, searchEnterRoute, searchIsReady, seeAllLabel, t,
  type SearchGroup, type SearchGroupResult, type SearchHit, type SearchModule,
  SETTINGS_ROUTE, DEFAULT_DATE_FORMAT, formatDateTime, type DateFormat, WHATS_NEW_ROUTE, MY_INFO_ROUTE, AVATAR_MENU_IDLE_MS,
} from '@bms2/shared';
import { Icon } from '../components/Icon';
import { Size, documentName, formatDate, passageParts } from '@bms2/shared';
import { Pill } from '../components/controls/Pill';
import { Wordmark } from './Wordmark';
import { useWhatsNewUnseen } from './whatsNewSeen';
import { GLOBAL_SEARCH_ASK_EVENT } from './globalSearchAsk';
import styles from './shell.module.css';
import { reportCaughtInBrowser } from './systemErrors';
import { suggestionDotFor } from '../components/composites/SuggestionDot';

/**
 * The Global Header (piece 12): fixed, non-scrolling, full width and 42px tall,
 * carrying the logo, global search and the avatar menu. There is no
 * notification bell, by design. In the test environment it also carries the
 * persistent Test environment banner, which reads the build stamp and the reset
 * record rather than a setting a person can switch.
 */
/** 🔒 The search names the module the chooser holds (decision 2770), and the shell asks only what that module needs. */
export type SearchFn = (term: string, signal: AbortSignal, module: SearchModule) => Promise<{ hits: readonly SearchHit[]; totals?: ReadonlyMap<SearchGroup, number>; seeAll?: ReadonlyMap<SearchGroup, string> }>;

export interface TestEnvironment {
  commit: string;
  lastResetAt: string | null;
}

/** What the avatar menu names as the running version: the deployed commit and when its bundle was built (decision 0358). */
export interface BuildStamp {
  commit: string;
  builtAt: string;
}

export interface GlobalHeaderProps {
  personName: string;
  initials: string;
  /** Present in the test build and absent in production; nothing a person can switch. */
  testEnvironment?: TestEnvironment | null;
  /** 🔨 The running version, named in every environment production included (decision 0358). */
  build?: BuildStamp | null;
  /** The zone and format the version line's build instant is drawn in; the header sits outside the frame's own provider. */
  timeZone?: string;
  dateFormat?: DateFormat;
  search?: SearchFn;
  /**
   * 🔒 This session is an external one (decision 0607): the header draws no
   * global search and no Settings gear, because the server answers neither to a
   * login outside the building. 🔨 Hiding them is presentation — the routes
   * refuse the request either way — and the flag comes from the shell's own read.
   */
  external?: boolean;
  onOpenDrawer?: () => void;
  onSignOut?: () => void;
  /** 🔨 Account settings was drawn and inert until the zone setting gave it something to carry. */
  onAccountSettings?: () => void;
  /** 🔒 A dot on the Account Settings row while a passkey is offered and not yet set up or dismissed (decision 1442; T693). */
  securityDot?: boolean;
  onViewAs?: () => void;
}

/**
 * The banner names the deployed commit and the date of the last Reset to
 * fixtures, built as one string so no separator is a bare literal in the markup.
 */
export function testBannerText({ commit, lastResetAt }: TestEnvironment, timeZone = 'UTC', dateFormat: DateFormat = DEFAULT_DATE_FORMAT): string {
  const parts = [t('shell.testEnvironment'), commit.slice(0, 7)];
  if (lastResetAt) parts.push(`${t('shell.lastReset')} ${formatDate(lastResetAt, timeZone, dateFormat)}`);
  return parts.join(' · ');
}

/**
 * The version line at the foot of the avatar menu: the deployed commit short,
 * and when the bundle was built, drawn in the viewer's own zone and the
 * Organization's date format. A build stamp that is not an instant, which is
 * what a developer's own `vite dev` leaves, names the commit alone.
 */
export function buildStampText({ commit, builtAt }: BuildStamp, zone: string, format: DateFormat = DEFAULT_DATE_FORMAT): string {
  const parts = [`${t('shell.version')} ${commit.slice(0, 7)}`];
  const at = new Date(builtAt);
  if (!Number.isNaN(at.getTime())) parts.push(`${t('shell.built')} ${formatDateTime(at, zone, format)}`);
  return parts.join(' \u00b7 ');
}

/** A module's name on the chooser: All in the person's words, every other module by its group's own name. */
export function searchModuleName(module: SearchModule): string {
  return module === SEARCH_ALL ? t('shell.searchModuleAll') : module;
}

export function GlobalHeader({ personName, initials, testEnvironment = null, build = null, timeZone = 'UTC', dateFormat = DEFAULT_DATE_FORMAT, search, external = false, onOpenDrawer, onSignOut, onViewAs, onAccountSettings, securityDot = false }: GlobalHeaderProps) {
  const [term, setTerm] = useState('');
  /**
   * 🔒 Decision 2770 (T1616): the module the search asks, All on a new page load. All never searches Settings, the
   * owner's cost concern of 2026-10-08; Settings is searched only when it is chosen here.
   */
  const [module, setModule] = useState<SearchModule>(SEARCH_ALL);
  const [moduleOpen, setModuleOpen] = useState(false);
  const moduleWrap = useRef<HTMLDivElement | null>(null);
  // The chooser is named for what it does, and reads the module it holds as its description.
  const moduleNameId = useId();
  useEffect(() => {
    if (!moduleOpen) return undefined;
    const outside = (event: PointerEvent) => {
      if (moduleWrap.current && event.target instanceof Node && !moduleWrap.current.contains(event.target)) setModuleOpen(false);
    };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [moduleOpen]);
  const [fetched, setFetched] = useState<SearchGroupResult[] | null>(null);
  const [pending, setPending] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  /**
   * 🔒 T353: the menu closes itself ten seconds after it opens with no pointer or
   * key inside it; a pointer, a key or a focus inside it starts the ten seconds
   * again. A ref holds the timer, so a moving pointer re-arms it without redrawing.
   */
  const menuIdle = useRef<ReturnType<typeof setTimeout> | null>(null);
  const armMenuIdle = useCallback(() => {
    if (menuIdle.current) clearTimeout(menuIdle.current);
    menuIdle.current = setTimeout(() => setMenuOpen(false), AVATAR_MENU_IDLE_MS);
  }, []);
  useEffect(() => {
    if (!menuOpen) return undefined;
    armMenuIdle();
    return () => {
      if (menuIdle.current) clearTimeout(menuIdle.current);
    };
  }, [menuOpen, armMenuIdle]);
  // 🔒 The dot on the avatar and on What's new, while this browser has not been shown the newest entry (decision 0636).
  const unseen = useWhatsNewUnseen();
  const panelId = useId();
  const abort = useRef<AbortController | null>(null);

  // Debounced, two characters minimum, and every in-flight request cancelled
  // before the next goes out: a stale answer overwriting a fresh one is the
  // defect the cancel exists to stop.
  const ready = search != null && searchIsReady(term);
  // The panel is open only while the term is long enough, so a short term needs
  // no reset: `results` is derived rather than stored back.
  const results = ready ? fetched : null;

  useEffect(() => {
    abort.current?.abort();
    if (!search || !searchIsReady(term)) return;
    const controller = new AbortController();
    abort.current = controller;
    const timer = setTimeout(() => {
      setPending(true);
      search(term, controller.signal, module)
        .then(({ hits, totals, seeAll }) => {
          if (controller.signal.aborted) return;
          setFetched(groupResults(hits, totals ?? new Map(), seeAll ?? new Map()));
          setHighlight(-1);
          setPending(false);
        })
        .catch((error: unknown) => {
          reportCaughtInBrowser('shell.header.read', error);
          if (!controller.signal.aborted) setPending(false);
        });
    }, SEARCH_PAUSE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [term, search, module]);

  const rows = useMemo(() => (results ?? []).flatMap((group) => group.rows), [results]);
  // 🔒 A page asks for a term (decision 1477): the field takes it and the focus, and the panel opens on its results.
  const searchField = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (external) return undefined;
    const ask = (event: Event) => {
      const term = (event as CustomEvent<{ term?: unknown }>).detail?.term;
      if (typeof term !== 'string') return;
      setTerm(term);
      // A page's ask is a search of the records, whatever module the chooser held.
      setModule(SEARCH_ALL);
      searchField.current?.focus();
    };
    window.addEventListener(GLOBAL_SEARCH_ASK_EVENT, ask);
    return () => window.removeEventListener(GLOBAL_SEARCH_ASK_EVENT, ask);
  }, [external]);

  return (
    <>
    <header className={styles.header} data-shell="header">
      <button type="button" className={`${styles.headerButton} ${styles.drawerToggle}`} onClick={onOpenDrawer} aria-label={t('shell.openMenu')} data-shell="drawer-toggle">
        <Icon meaning="mainMenu" size="header" />
      </button>
      {/*
        🔨 The wordmark is the brand's own, drawn from the mark the product
        owner supplied. 💰 The header carried a dashboard glyph beside the
        product code instead, which is neither the logo nor a link anyone
        recognises (found while building, 2026-09-09).
      */}
      <Link to="/" className={styles.logo} data-shell="logo" aria-label={t('shell.productName')}>
        <Wordmark />
      </Link>

      {/* 🔒 An external session searches nothing: the search reads every module's records and the route answers it no rows at all (decision 0607). */}
      {external ? null : (
      <div className={styles.headerSearch}>
        <div className={styles.searchModuleWrap} ref={moduleWrap}>
          <button
            type="button"
            className={styles.searchModule}
            aria-haspopup="menu"
            aria-expanded={moduleOpen}
            aria-label={t('shell.searchModuleLabel')}
            aria-describedby={moduleNameId}
            data-shell="search-module"
            data-search-module-chosen={module}
            onClick={() => setModuleOpen((open) => !open)}
          >
            <span className={styles.searchModuleName} id={moduleNameId}>{searchModuleName(module)}</span>
            <Icon meaning="expand" size="inline" />
          </button>
          {moduleOpen && (
            <div
              className={`${styles.searchPanel} ${styles.searchModuleMenu}`}
              role="menu"
              data-shell="search-module-menu"
              onKeyDown={(event) => {
                if (event.key === 'Escape') setModuleOpen(false);
              }}
            >
              {SEARCH_MODULES.map((choice) => (
                <button
                  key={choice}
                  type="button"
                  role="menuitemradio"
                  aria-checked={choice === module}
                  className={styles.searchRow}
                  data-search-module={choice}
                  onClick={() => {
                    setModule(choice);
                    setModuleOpen(false);
                    searchField.current?.focus();
                  }}
                >
                  {searchModuleName(choice)}
                </button>
              ))}
            </div>
          )}
        </div>
        <input
          ref={searchField}
          type="search"
          className={styles.searchInput}
          value={term}
          placeholder={module === SEARCH_ALL ? t('shell.searchPlaceholder') : `${t('shell.searchIn')} ${searchModuleName(module)}`}
          aria-label={t('shell.searchLabel')}
          aria-controls={panelId}
          aria-expanded={results !== null}
          data-shell="search"
          onChange={(event) => setTerm(event.target.value)}
          onKeyDown={(event) => {
            if (rows.length === 0) return;
            if (event.key === 'Enter') {
              // T3332: a highlighted row opens its record; with none highlighted the first group's See all opens its filtered list.
              const to = rows[highlight]?.route ?? searchEnterRoute(results ?? [], module);
              if (!to) return;
              event.preventDefault();
              setTerm('');
              navigate(to);
            } else if (event.key === 'ArrowDown') {
              event.preventDefault();
              setHighlight((h) => Math.min(h + 1, rows.length - 1));
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setHighlight((h) => Math.max(h - 1, 0));
            } else if (event.key === 'Escape') {
              setTerm('');
            }
          }}
        />
        {/* 🔨 Picking a result clears the term, which is what closes the panel: the
            page navigates and the search is empty for the next look. 💰 The panel
            stayed open over the record it had just opened (found while building,
            2026-09-09). */}
        {results !== null && (
          <div className={styles.searchPanel} id={panelId} role="listbox" data-shell="search-panel">
            {results.length === 0 && <p className={styles.searchEmpty}>{pending ? t('shell.searchLoading') : t('shell.searchEmpty')}</p>}
            {results.map((group) => (
              <section className={styles.searchGroup} key={group.group} data-search-group={group.group}>
                <header className={styles.searchGroupHead}>
                  <span>{group.group}</span>
                  {group.seeAllRoute && <Link to={group.seeAllRoute} onClick={() => setTerm('')}>{seeAllLabel(group)}</Link>}
                </header>
                {group.rows.map((row) => (
                  <Link
                    key={row.id}
                    to={row.route}
                    className={styles.searchRow}
                    role="option"
                    aria-selected={rows[highlight]?.id === row.id}
                    data-search-row={row.id}
                    onClick={() => setTerm('')}
                  >
                    {row.documentType && <Pill text={documentName(row.documentType)} foreground="var(--status-inactive-text)" background="var(--status-inactive-bg)" size={Size.Small} />}
                    <span>{row.label}</span>
                    {/* 🔒 T3028: a person or company hit whose latest checks hold an open suggestion carries the gold dot. */}
                    {suggestionDotFor(row.suggestions, group.group === 'Companies' ? 'company' : 'person')}
                    {/* 🔒 Piece 30: a knowledge-base hit shows the matching passage as its second line, not only the title. */}
                    {row.passage ? <small data-search-passage="true">{passageParts(row.passage).map((part, index) => (part.hit ? <mark key={index}>{part.text}</mark> : <span key={index}>{part.text}</span>))}</small> : null}
                  </Link>
                ))}
              </section>
            ))}
          </div>
        )}
      </div>
      )}

      <span className={styles.spacer} />

      {testEnvironment && (
        <span className={styles.testBanner} data-shell="test-banner">{testBannerText(testEnvironment, timeZone, dateFormat)}</span>
      )}

      {/* The gear opens the Settings Index (piece 15); Account settings stays in the avatar menu. 🔒 An external session reaches no settings screen, so it is not offered one (decision 0607). */}
      {external ? null : (
        <Link to={SETTINGS_ROUTE} className={styles.headerButton} aria-label={t('shell.settings')} title={t('shell.settings')} data-shell="settings">
          <Icon meaning="settings" size="header" />
        </Link>
      )}
      <div className={styles.avatarWrap}>
        <button
          type="button"
          className={styles.headerButton}
          onClick={() => setMenuOpen((open) => !open)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          title={personName}
          aria-label={unseen ? `${personName} · ${t('shell.whatsNewUnseen')}` : undefined}
          data-shell="avatar"
          data-whats-new-unseen={unseen ? 'true' : 'false'}
        >
          <span className={styles.avatarName}>{personName}</span>
          <span className={styles.avatarBadge} aria-hidden>
            {initials}
            {unseen ? <span className={styles.whatsNewDot} data-shell="avatar-whats-new-dot" /> : null}
          </span>
        </button>
        {menuOpen && (
          <div className={`${styles.searchPanel} ${styles.avatarMenu}`} role="menu" data-shell="avatar-menu" onPointerMove={armMenuIdle} onPointerDown={armMenuIdle} onKeyDown={armMenuIdle} onFocus={armMenuIdle}>
            <button type="button" className={styles.searchRow} role="menuitem" onClick={() => { setMenuOpen(false); onAccountSettings?.(); }} data-shell="account-settings">
              {t('shell.accountSettings')}
              {securityDot ? <span className={styles.securityDot} role="img" aria-label={t('passkey.offer.dot')} data-shell="account-settings-dot" /> : null}
            </button>
            {/* 🔒 Decision 3180: My Info, the signed-in person's own employee file, beside Account Settings; an outside login holds no job here. */}
            {!external && (
              <Link to={MY_INFO_ROUTE} className={styles.searchRow} role="menuitem" onClick={() => setMenuOpen(false)} data-shell="my-info">
                {t('shell.myInfo')}
              </Link>
            )}
            {/* 🔨 View as is a super admin's (decision 0349): with nobody to hand the click to, the item is absent rather than opening an empty picker. */}
            {onViewAs && <button type="button" className={styles.searchRow} role="menuitem" onClick={() => { setMenuOpen(false); onViewAs(); }} data-shell="view-as">{t('shell.viewAs')}</button>}
            {/* 🔨 Switch Company is not drawn here. It was a menu row carrying no
                handler at all, so it read as an offer and did nothing, and the
                picker over the viewer's own employments belongs to Settings and
                administration (decision 0559; found while building, 2026-09-18).
                The row comes back with the picker behind it. */}
            {/* 🔨 T382: the developer index row is gone from this menu (decision 1150).
                The page it pointed at stays reachable by its own address for the
                build's walk and the capture harness; nothing else links to it. */}
            <button type="button" className={styles.searchRow} role="menuitem" onClick={onSignOut}>
              <Icon meaning="signOut" size="inline" />
              {t('shell.signOut')}
            </button>
            {/* 🔒 What's new stands directly above the version line (decision 0636): the two answer what this build is and what it changed. */}
            <Link to={WHATS_NEW_ROUTE} className={styles.searchRow} role="menuitem" onClick={() => setMenuOpen(false)} data-shell="whats-new">
              {t('shell.whatsNew')}
              {unseen ? <span className={styles.whatsNewDot} role="img" aria-label={t('shell.whatsNewUnseen')} data-shell="menu-whats-new-dot" /> : null}
            </Link>
            {/* 🔨 The version reads, and is not a control: it carries no role and
                nothing happens when it is pressed (decision 0358). */}
            {build && (
              <p className={styles.buildStamp} data-shell="build-stamp">{buildStampText(build, timeZone, dateFormat)}</p>
            )}
          </div>
        )}
      </div>
    </header>
    {/* 🔨 The banner is persistent on a phone too: below md it drops out of the
        bar, where it left the search field no room, and stands as its own strip
        beneath (piece 12). One string, drawn in whichever place has the room. */}
    {testEnvironment && (
      <div className={styles.testStrip} data-shell="test-strip">{testBannerText(testEnvironment, timeZone, dateFormat)}</div>
    )}
    </>
  );
}
